import { View } from "react-native";
import MainLayoutWithSideNav from "../../components/Layout/MainLayoutWithSideNav";
import { routeSearchScreen } from "../../Routes";
import { useFocusEffect, useNavigation } from "@react-navigation/native";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getTransactionFilters, globalAny, recordAnalytics, refreshPage } from "../../utils/Utils";
import { Carousel, Categories, Pages } from "../../Types";
import PlaylistContainer from "../../components/Layout/PlaylistContainer";
import useGenericContentStore from "../../store/genericContent.store";
import { AppLogger } from "../../utils/AppLogger";
import { getCategories } from "../../services/categoriesService";
import CustomizeLoader from "../../components/Loaders/CustomizeLoader";
import Banner from "./Banner";
import { RetryCommand } from "../../enums/RetryCommand";
import useRetryCommand from "../../hooks/userRetryCommand";
import { StyledLoaderContent } from "./styled";
import { EventType } from "../../enums/EventType";

const packshots = Array.from({ length: 15 }, (_, i) => i + 1);

const defaultItem = [
	{ id: 1, isLandscape: true, packshot: packshots },
	{ id: 2, isLandscape: false, packshot: packshots },
];

const MyLibrary = () => {
	// Navigation
	const navigation = useNavigation();

	// Library state
	const [categories, setCategories] = useState([]);
	const [popular, setPopular] = useState<Carousel[]>([]);
	const [startIndex, setStartIndex] = useState(4);
	const [isShowLoader, setIsShowLoader] = useState(true);
	const [isMyLibraryEmpty, setIsMyLibraryEmpty] = useState(false);
	const [isContinueWatching, setIsContinueWatching] = useState(true);

	// Global store selectors
	const { isNarrow, setNavigateEvent, isRenderHome, setRetryCommand } = useGenericContentStore((state: any) => ({
		isNarrow: state.isNarrow,
		setNavigateEvent: state.setNavigateEvent,
		isRenderHome: state.isRenderHome,
		setRetryCommand: state.setRetryCommand,
	}));

	// Transactional filters
	const transactionalFilters = getTransactionFilters();

	/**
	 * Navigates to the search screen when called.
	 */
	const onSearch = () => {
		//@ts-ignore
		navigation.navigate(routeSearchScreen);
	};

	/**
	 * Memoized rendering of the MyLibrary playlist container.
	 *
	 * - Re-renders only when `categories`, `startIndex`, or `isShowLoader` change.
	 * - Passes down library-specific props to `PlaylistContainer`,
	 *   including handlers for loader state, pagination, and empty-state checks.
	 */
	const renderMyLibraryPlaylist = useMemo(
		() => (
			<PlaylistContainer
				categories={categories}
				isSeeMore
				isLibrary
				location={Categories.myLibrary}
				setIsShowLoader={setIsShowLoader}
				startIndex={startIndex}
				setStartIndex={setStartIndex}
				transactionalFilters={transactionalFilters}
				setIsMyLibraryEmpty={setIsMyLibraryEmpty}
				setIsContinueWatching={setIsContinueWatching}
			/>
		),
		[categories, startIndex, isShowLoader],
	);

	/**
	 * Fetches and updates "My Library" categories data.
	 *
	 * - Clears existing categories before fetching.
	 * - Creates an AbortController for request cancellation support.
	 * - Calls `getCategories` with the `myLibrary` category and transactional filters.
	 * - Updates state: categories, navigation event (to library), and clears popular list.
	 * - Logs errors if the fetch fails.
	 */
	const fetchMyLibraryData = async () => {
		setCategories([]);
		const controller = new AbortController();

		try {
			const categories: any = (await getCategories(Categories.myLibrary, transactionalFilters, controller.signal)) || [];
			setCategories(categories);
			setNavigateEvent(Pages.library);
			setPopular([]);
		} catch (err) {
			AppLogger.log(err);
		}
	};

	/**
	 * Effect to fetch MyLibrary data when the Home screen is rendered.
	 *
	 * Triggers whenever `isRenderHome` changes:
	 * - If `isRenderHome` is true, it calls `fetchMyLibraryData()`.
	 */
	useEffect(() => {
		if (isRenderHome) fetchMyLibraryData();
	}, [isRenderHome]);

	/**
	 * Effect to initialize and clean up MyLibrary data on component mount/unmount.
	 *
	 * On mount:
	 * - Sets the retry command to `MyLibrary`.
	 * - Fetches MyLibrary data.
	 *
	 * On unmount:
	 * - Clears categories.
	 * - Resets navigation event.
	 */
	useEffect(() => {
		setRetryCommand(RetryCommand.MyLibrary);
		fetchMyLibraryData();
		return () => {
			setCategories([]);
			setNavigateEvent("");
		};
	}, []);

	/**
	 * Effect to control the loader state based on library content.
	 * If the library is empty, hide the loader.
	 */
	useEffect(() => {
		if (isMyLibraryEmpty) setIsShowLoader(false);
	}, [isMyLibraryEmpty]);

	useFocusEffect(
		useCallback(() => {
			recordAnalytics(EventType.ScreenView, { screen_name: Pages.myLibrary });
		}, []),
	);

	useRetryCommand(RetryCommand.MyLibrary, refreshPage);
	return (
		<MainLayoutWithSideNav
			searchIcon={true}
			screenTitle={globalAny.language.header_my_library}
			navTitle={"MyLibrary"}
			onPressRightIcon={onSearch}
			isNarrow={isNarrow}
		>
			<StyledLoaderContent isShowLoader={isShowLoader} isNarrow={isNarrow}>
				<CustomizeLoader show={isShowLoader} isPlaylist={true} data={defaultItem} />
			</StyledLoaderContent>
			{!isContinueWatching && isMyLibraryEmpty ? (
				<View style={{ paddingTop: 112, paddingLeft: 112 }}>
					<Banner data={popular} />
				</View>
			) : (
				<></>
			)}
			{renderMyLibraryPlaylist}
		</MainLayoutWithSideNav>
	);
};

export default MyLibrary;
